import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Modal, Button, Form, Badge } from "react-bootstrap";
import { Droplet, MapPin, Search, Filter, Trash2, AlertTriangle, Clock, Phone, Building2 } from "lucide-react";
import "../../../assets/css/donordashboard/DonateBlood.css";
import MasterLayout from "../../../components/layout/MasterLayout";
import axios from "axios";
import Swal from "sweetalert2";
import AppContext from "../../../context/AppContext";
import { useContext } from "react";

const DonateBlood = () => {
  const { baseUrl, loggedInDonorId } = useContext(AppContext);

  // ── Donor Donations ──
  const [donation, setDonation] = useState([]);

  // ── Receiver Requests ──
  const [receiverRequests, setReceiverRequests] = useState([]);

  // ── Table Filter States ──
  const [tableSearch, setTableSearch]             = useState("");
  const [tableStatusFilter, setTableStatusFilter] = useState("");
  const [bloodFilter, setBloodFilter]             = useState("");

  // ── Modal States ──
  const [showModal, setShowModal]               = useState(false);
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [donorName, setDonorName]               = useState("");
  const [units, setUnits]                       = useState("");
  const [donateDate, setDonateDate]             = useState("");
  const [modalLoading, setModalLoading]         = useState(false);

  // ── Confirmed IDs ──
  const [confirmedIds, setConfirmedIds] = useState([]);

  useEffect(() => {
    if (loggedInDonorId) {
      ViewAllDonation();
      ViewReceiverRequests();
    }
  }, [loggedInDonorId]);

  // ========================= VIEW ALL DONATIONS =========================
  const ViewAllDonation = () => {
    axios
      .get(`${baseUrl}/donor/dashboard/viewAllBloodDonations`, {
        params: { donor_id: loggedInDonorId },
      })
      .then((res) => {
        if (res.data.success) {
          const data = res.data.donation || [];
          setDonation(data);
          const alreadyConfirmed = data
            .filter((d) => d.blood_units && d.blood_date)
            .map((d) => d.id);
          setConfirmedIds(alreadyConfirmed);
        }
      })
      .catch((err) => console.log(err));
  };

  // ========================= VIEW RECEIVER REQUESTS =========================
  const ViewReceiverRequests = () => {
    axios
      .get(`${baseUrl}/receiver/blood/viewAllRequests`, {
        params: { donor_id: loggedInDonorId },
      })
      .then((res) => {
        if (res.data.success) {
          const requests = res.data.requests || [];
          setReceiverRequests(requests);

          const approvedIds = requests
            .filter((r) => (r.status || "").toLowerCase() === "approved")
            .map((r) => r.id);

          setConfirmedIds((prev) => [...new Set([...prev, ...approvedIds])]);
        }
      })
      .catch((err) => console.log(err));
  };

  // ========================= DELETE DONATION =========================
  const DeleteDonation = (id) => {
    Swal.fire({
      title: "Are You Sure?",
      text: "Donation will be deleted permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
    }).then((result) => {
      if (result.isConfirmed) {
        const fd = new FormData();
        fd.append("id",       id);
        fd.append("donor_id", loggedInDonorId);
        axios
          .post(`${baseUrl}/donor/dashboard/deleteBloodDonation`, fd)
          .then((res) => {
            if (res.data.success) {
              Swal.fire({ title: "Deleted!", text: "Blood donation deleted.", icon: "success" });
              ViewAllDonation();
            }
          })
          .catch((err) => console.log(err));
      }
    });
  };

  // ========================= OPEN MODAL (donor donation) =========================
  const handleDonateNow = (don) => {
    setSelectedDonation({
      id:            don.id,
      blood_group:   don.blood_group,
      donation_type: don.donation_type || "Whole Blood",
      city:          don.city,
      _isReceiver:   false,
    });
    setDonorName(""); setUnits(""); setDonateDate("");
    setShowModal(true);
  };

  // ========================= OPEN MODAL (receiver request) =========================
  const handleDonateToReceiver = (req) => {
    setSelectedDonation({
      id:             req.id,
      blood_group:    req.blood_group,
      donation_type:  "Whole Blood",
      city:           req.city,
      patient_name:   req.patient_name,
      hospital:       req.hospital,
      units_required: req.units,
      _isReceiver:    true,
    });
    setDonorName(""); setUnits(""); setDonateDate("");
    setShowModal(true);
  };

  // ========================= CONFIRM DONATION =========================
  const handleConfirmDonate = () => {
    if (!donorName.trim() || !units || !donateDate) {
      Swal.fire({ title: "Required!", text: "Please fill all fields", icon: "warning" });
      return;
    }
    setModalLoading(true);

    const fd = new FormData();
    fd.append("id",          selectedDonation.id);
    fd.append("donor_id",    loggedInDonorId);
    fd.append("donor_name",  donorName);
    fd.append("blood_units", units);
    fd.append("blood_date",  donateDate);

    axios
      .post(`${baseUrl}/donor/dashboard/confirmBloodDonation`, fd)
      .then((res) => {
        if (res.data.success) {
          setConfirmedIds((prev) => [...prev, selectedDonation.id]);
          setShowModal(false);
          Swal.fire({ title: "Confirmed! 🩸", text: "Donation confirmed successfully!", icon: "success", confirmButtonColor: "#198754" });
          ViewAllDonation();
          ViewReceiverRequests();
        } else {
          Swal.fire({ title: "Error!", text: res.data.message || "Could not confirm.", icon: "error" });
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setModalLoading(false));
  };

  // ========================= HELPERS =========================
  const getStatusClass = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "pending")   return "bdt-status-pill bdt-status-pending";
    if (s === "verified")  return "bdt-status-pill bdt-status-verified";
    if (s === "collected") return "bdt-status-pill bdt-status-collected";
    return "bdt-status-pill bdt-status-pending";
  };

  const getUrgencyBadge = (urgency) => {
    const u = (urgency || "").toLowerCase();
    if (u === "emergency") return (
      <Badge bg="danger" style={{ display:"flex", alignItems:"center", gap:"3px", width:"fit-content" }}>
        <AlertTriangle size={10} /> Emergency
      </Badge>
    );
    if (u === "routine") return (
      <Badge bg="info" style={{ display:"flex", alignItems:"center", gap:"3px", width:"fit-content" }}>
        <Clock size={10} /> Routine
      </Badge>
    );
    return <span style={{ color: "#aaa", fontSize: "0.8rem" }}>—</span>;
  };

  const getBloodGroupColor = (group) => {
    const colors = {
      "O-":"#dc2626","O+":"#ef4444","A-":"#f97316","A+":"#fb923c",
      "B-":"#8b5cf6","B+":"#a78bfa","AB-":"#06b6d4","AB+":"#22d3ee",
    };
    return colors[group] || "#6b7280";
  };

  // ── Merge donor donations + receiver requests into one list ──
  const donorRows    = donation.map((d)    => ({ ...d, _rowType: "donor"    }));
  const receiverRows = receiverRequests.map((r) => ({ ...r, _rowType: "receiver" }));
  const allRows      = [...donorRows, ...receiverRows];

  const filteredRows = allRows.filter((row) => {
    const matchSearch =
      tableSearch === "" ||
      Object.values(row).some((v) =>
        String(v).toLowerCase().includes(tableSearch.toLowerCase())
      );
    const matchStatus =
      tableStatusFilter === "" ||
      (row.status || "").toLowerCase() === tableStatusFilter.toLowerCase();
    const matchBlood =
      bloodFilter === "" || row.blood_group === bloodFilter;

    return matchSearch && matchStatus && matchBlood;
  });

  // ========================= RETURN =========================
  return (
    <MasterLayout>
      <div className="blood-dashboard-container">
        <Container fluid>
          <Row className="blood-section-row">
            <Col lg={12}>
              <Card className="bdt-card">

                {/* ── Table Header ── */}
                <div className="bdt-header">
                  <div className="bdt-title">
                    <Droplet size={20} className="bdt-title-icon" />
                    My Blood Donations
                    <span className="bdt-count-badge">{filteredRows.length}</span>
                  </div>

                  <div className="bdt-controls">
                    <div className="bdt-search-wrap">
                      <Search size={14} className="bdt-search-icon" />
                      <input
                        type="text" className="bdt-search" placeholder="Search..."
                        value={tableSearch} onChange={(e) => setTableSearch(e.target.value)}
                      />
                    </div>

                    <div className="bdt-filter-wrap">
                      <Droplet size={14} className="bdt-filter-icon" />
                      <select className="bdt-filter" value={bloodFilter} onChange={(e) => setBloodFilter(e.target.value)}>
                        <option value="">All Blood Groups</option>
                        {["A+","A-","B+","B-","O+","O-","AB+","AB-"].map((g) => (
                          <option key={g} value={g}>{g}</option>
                        ))}
                      </select>
                    </div>

                    <div className="bdt-filter-wrap">
                      <Filter size={14} className="bdt-filter-icon" />
                      <select className="bdt-filter" value={tableStatusFilter} onChange={(e) => setTableStatusFilter(e.target.value)}>
                        <option value="">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="verified">Verified</option>
                        <option value="collected">Collected</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* ── Table ── */}
                <div className="bdt-table-wrap">
                  <table className="bdt-table">
                    <thead>
                      <tr>
                        <th className="bdt-th">#</th>
                        <th className="bdt-th">Type</th>
                        <th className="bdt-th">Blood Group</th>
                        <th className="bdt-th">Donation Type</th>
                        <th className="bdt-th">Quantity</th>
                        <th className="bdt-th">City</th>
                        <th className="bdt-th">Patient Name</th>
                        <th className="bdt-th">Hospital</th>
                        <th className="bdt-th">Urgency</th>
                        <th className="bdt-th">Phone</th>
                        <th className="bdt-th">Status</th>
                        <th className="bdt-th bdt-th-center">Donate</th>
                        <th className="bdt-th bdt-th-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.length > 0 ? (
                        filteredRows.map((row, index) => {
                          const isConfirmed = confirmedIds.includes(row.id);
                          const isReceiver  = row._rowType === "receiver";

                          return (
                            <tr
                              key={`${row._rowType}-${row.id || index}`}
                              className="bdt-tr"
                              style={isReceiver ? { background: "#fff8f8" } : {}}
                            >
                              <td className="bdt-td bdt-idx">{index + 1}</td>

                              <td className="bdt-td">
                                {isReceiver ? (
                                  <Badge bg="danger" style={{ fontSize: "0.7rem" }}>🩸 Request</Badge>
                                ) : (
                                  <Badge bg="success" style={{ fontSize: "0.7rem" }}>✅ Donation</Badge>
                                )}
                              </td>

                              <td className="bdt-td">
                                <span style={{
                                  display: "inline-flex", alignItems: "center", gap: "4px",
                                  background: getBloodGroupColor(row.blood_group),
                                  color: "#fff", borderRadius: "20px",
                                  padding: "2px 9px", fontSize: "0.78rem", fontWeight: 700,
                                }}>
                                  <Droplet size={12} />
                                  {row.blood_group || "—"}
                                </span>
                              </td>

                              <td className="bdt-td" style={{ fontSize: "0.83rem" }}>
                                {isReceiver ? "Whole Blood" : (row.donation_type || "Whole Blood")}
                              </td>

                              <td className="bdt-td" style={{ fontSize: "0.83rem" }}>
                                {isReceiver
                                  ? <strong style={{ color: "#dc2626" }}>{row.units} unit{row.units > 1 ? "s" : ""}</strong>
                                  : (row.quantity || "1 Pint")
                                }
                              </td>

                              <td className="bdt-td">
                                <div className="bdt-hospital-cell">
                                  <MapPin size={13} className="bdt-loc-icon" />
                                  {row.city || "—"}
                                </div>
                              </td>

                              <td className="bdt-td" style={{ fontSize: "0.83rem" }}>
                                {isReceiver
                                  ? <strong>{row.patient_name || "—"}</strong>
                                  : <span style={{ color: "#ccc" }}>—</span>
                                }
                              </td>

                              <td className="bdt-td">
                                {isReceiver ? (
                                  <div style={{ display:"flex", alignItems:"center", gap:"4px", fontSize:"0.82rem" }}>
                                    <Building2 size={12} style={{ color:"#6b7280" }} />
                                    {row.hospital || "—"}
                                  </div>
                                ) : <span style={{ color: "#ccc" }}>—</span>}
                              </td>

                              <td className="bdt-td">
                                {isReceiver
                                  ? getUrgencyBadge(row.urgency)
                                  : <span style={{ color: "#ccc" }}>—</span>
                                }
                              </td>

                              <td className="bdt-td">
                                {isReceiver ? (
                                  <div style={{ display:"flex", alignItems:"center", gap:"4px", fontSize:"0.82rem" }}>
                                    <Phone size={12} style={{ color:"#6b7280" }} />
                                    {row.phone || "—"}
                                  </div>
                                ) : <span style={{ color: "#ccc" }}>—</span>}
                              </td>

                              <td className="bdt-td">
                                <span className={getStatusClass(row.status)}>
                                  {row.status || "Pending"}
                                </span>
                              </td>

                              <td className="bdt-td bdt-td-center">
                                {isConfirmed ? (
                                  <button disabled style={{
                                    background: "#198754", color: "#fff", border: "none",
                                    borderRadius: "8px", padding: "5px 12px",
                                    fontSize: "0.78rem", fontWeight: 600,
                                    cursor: "not-allowed", whiteSpace: "nowrap",
                                  }}>
                                    ✅ Confirmed
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => isReceiver ? handleDonateToReceiver(row) : handleDonateNow(row)}
                                    style={{
                                      background: isReceiver
                                        ? "linear-gradient(135deg,#dc2626,#b91c1c)"
                                        : "#dc3545",
                                      color: "#fff", border: "none",
                                      borderRadius: "8px", padding: "5px 12px",
                                      fontSize: "0.78rem", fontWeight: 600,
                                      cursor: "pointer", whiteSpace: "nowrap",
                                      boxShadow: isReceiver ? "0 2px 6px rgba(220,38,38,0.35)" : "none",
                                    }}
                                  >
                                    🩸 Donate Now
                                  </button>
                                )}
                              </td>

                              <td className="bdt-td bdt-td-center">
                                {!isReceiver ? (
                                  <div className="bdt-action-btns">
                                    <button
                                      className="bdt-delete-btn"
                                      onClick={() => DeleteDonation(row.id)}
                                      title="Delete"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                ) : (
                                  <span style={{ color: "#ccc" }}>—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={13} className="bdt-empty">
                            <div className="bdt-empty-inner">
                              <Droplet size={40} className="bdt-empty-icon" />
                              <p className="bdt-empty-text">No records found</p>
                              <p className="bdt-empty-sub">
                                {tableSearch || tableStatusFilter || bloodFilter
                                  ? "Try changing your search or filter."
                                  : "No blood donations or requests yet. 🩸"}
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Footer */}
                {filteredRows.length > 0 && (
                  <div className="bdt-footer">
                    <span className="bdt-footer-text">
                      Showing <strong>{filteredRows.length}</strong> of <strong>{allRows.length}</strong> records
                      &nbsp;|&nbsp;
                      <span style={{ color: "#198754" }}>✅ {donorRows.length} Donations</span>
                      &nbsp;&nbsp;
                      <span style={{ color: "#dc3545" }}>🩸 {receiverRows.length} Requests</span>
                    </span>
                  </div>
                )}

              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* ========================= MODAL ========================= */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered backdrop="static">
        <Modal.Header closeButton style={{ background: "#dc3545", color: "#fff", borderBottom: "none" }}>
          <Modal.Title style={{ fontWeight: 700, fontSize: "1.1rem" }}>
            🩸 Confirm Blood Donation
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "1.8rem" }}>
          {selectedDonation && (
            <div style={{
              background: "#fff5f5", border: "1px solid #f5c6cb",
              borderRadius: "10px", padding: "12px 16px", marginBottom: "1.4rem",
              display: "flex", gap: "1.2rem", flexWrap: "wrap",
            }}>
              <div>
                <small style={{ color: "#888" }}>Blood Group</small>
                <div style={{ fontWeight: 700, color: "#dc3545", fontSize: "1.1rem" }}>
                  {selectedDonation.blood_group || "—"}
                </div>
              </div>
              <div>
                <small style={{ color: "#888" }}>City</small>
                <div style={{ fontWeight: 600 }}>{selectedDonation.city || "—"}</div>
              </div>
              {selectedDonation._isReceiver && (
                <>
                  <div>
                    <small style={{ color: "#888" }}>Patient</small>
                    <div style={{ fontWeight: 600 }}>{selectedDonation.patient_name || "—"}</div>
                  </div>
                  <div>
                    <small style={{ color: "#888" }}>Hospital</small>
                    <div style={{ fontWeight: 600 }}>{selectedDonation.hospital || "—"}</div>
                  </div>
                  <div>
                    <small style={{ color: "#888" }}>Units Needed</small>
                    <div style={{ fontWeight: 600, color: "#dc2626" }}>{selectedDonation.units_required || "—"}</div>
                  </div>
                </>
              )}
            </div>
          )}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>
                Donor Name <span style={{ color: "red" }}>*</span>
              </Form.Label>
              <Form.Control
                type="text" placeholder="Enter your full name"
                value={donorName} onChange={(e) => setDonorName(e.target.value)}
                style={{ borderRadius: "8px" }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>
                Units to Donate <span style={{ color: "red" }}>*</span>
              </Form.Label>
              <Form.Select value={units} onChange={(e) => setUnits(e.target.value)} style={{ borderRadius: "8px" }}>
                <option value="">Select Units</option>
                {[1,2,3,4,5].map((n) => (
                  <option key={n} value={n}>{n} Unit{n > 1 ? "s" : ""}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-1">
              <Form.Label style={{ fontWeight: 600 }}>
                Donation Date <span style={{ color: "red" }}>*</span>
              </Form.Label>
              <Form.Control
                type="date" value={donateDate}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => setDonateDate(e.target.value)}
                style={{ borderRadius: "8px" }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer style={{ borderTop: "none", padding: "0 1.8rem 1.5rem" }}>
          <Button
            variant="outline-secondary"
            onClick={() => setShowModal(false)}
            style={{ borderRadius: "8px", minWidth: "100px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDonate} disabled={modalLoading}
            style={{ background: "#dc3545", border: "none", borderRadius: "8px", minWidth: "160px", fontWeight: 600 }}
          >
            {modalLoading ? "Confirming..." : "🩸 Confirm Donation"}
          </Button>
        </Modal.Footer>
      </Modal>

    </MasterLayout>
  );
};

export default DonateBlood;